import React, { forwardRef, type Ref, useContext } from "react";
import type { FieldRenderProps } from "react-final-form";
import { Input, type InputProps } from "reactstrap";
import { FormLabelRenderer } from "./FormLabelRenderer";
import FormRendererNew, {
  type IFormRendererCombinedProps,
} from "./FormRendererNew";
import { InputRendererContext } from "./InputRenderer";

export interface IInputRendererNewProps<
  FieldValue extends string | number,
  T extends HTMLInputElement,
> extends FieldRenderProps<FieldValue, T>,
    IFormRendererCombinedProps<FieldValue, T>,
    InputProps {}

const InputRendererNew = forwardRef(
  <FieldValue extends string | number, T extends HTMLInputElement>(
    {
      input,
      meta,
      label,
      type,
      formText,
      tooltip,
      alwaysShowLabel,
      disableErrors,
      hideLabel,
      ...rest
    }: IInputRendererNewProps<FieldValue, T>,
    ref: Ref<HTMLInputElement>,
  ) => {
    const { error, submitError, submitting } = meta;
    const contextProps = useContext(InputRendererContext);

    return (
      <FormRendererNew<FieldValue, T>
        input={input}
        meta={meta}
        label={label}
        type={type}
        formText={formText}
        tooltip={tooltip}
        alwaysShowLabel={alwaysShowLabel}
        disableErrors={disableErrors}
        hideLabel={hideLabel}
      >
        <FormLabelRenderer label={label}>
          {(placeholderText) => (
            <Input
              {...rest}
              {...contextProps}
              placeholder={placeholderText}
              invalid={Boolean(!disableErrors && (error || submitError))}
              disabled={rest.disabled || submitting}
              {...input}
              type={type}
              innerRef={ref}
            />
          )}
        </FormLabelRenderer>
      </FormRendererNew>
    );
  },
);
InputRendererNew.displayName = "InputRendererNew";
export default InputRendererNew;
